/**
 * Financial Planning Service - CFO Agent
 * Endpoints: /api/governance/finance/planning/*
 */
import { AZURE } from './api';

const BASE = '/governance/finance/planning';

// Dashboard
export const getFinancialDashboard = async () => {
  const { data } = await AZURE.get(`${BASE}/dashboard`);
  return data;
};

// Scenarios
export const listScenarios = async () => {
  const { data } = await AZURE.get(`${BASE}/scenarios`);
  return data;
};

export const getScenarioCatalog = async () => {
  const { data } = await AZURE.get(`${BASE}/scenarios/catalog`);
  return data;
};

export const getScenario = async (id) => {
  const { data } = await AZURE.get(`${BASE}/scenarios/${id}`);
  return data;
};

export const createScenario = async (payload) => {
  const { data } = await AZURE.post(`${BASE}/scenarios`, payload);
  return data;
};

// Assumptions
export const listAssumptions = async (params = {}) => {
  const { data } = await AZURE.get(`${BASE}/assumptions`, { params });
  return data;
};

export const upsertAssumption = async (payload) => {
  const { data } = await AZURE.put(`${BASE}/assumptions`, payload);
  return data;
};

export const deleteAssumption = async (id) => {
  const { data } = await AZURE.delete(`${BASE}/assumptions/${id}`);
  return data;
};

// Expenses
/**
 * Lista despesas. Aceita params livres OU forma normalizada:
 *   listExpenses({ referenceMonth: '2026-06', category: 'cloud', scenario_type: 'actual' })
 *
 * Backend (routes_financial_planning.expenses_list) aceita os dois aliases
 * 'reference_month' e 'month'. Mandamos os DOIS por compat:
 *  - 'reference_month' = canônico (alinhado com competência mensal)
 *  - 'month' = legacy (mantém qualquer caller antigo funcionando)
 */
export const listExpenses = async (params = {}) => {
  const { referenceMonth, reference_month, month, ...rest } = params;
  const m = referenceMonth || reference_month || month || undefined;
  const finalParams = { ...rest };
  if (m) {
    finalParams.reference_month = m;
    finalParams.month = m;
  }
  const { data } = await AZURE.get(`${BASE}/expenses`, { params: finalParams });
  return data;
};

export const getExpense = async (id) => {
  const { data } = await AZURE.get(`${BASE}/expenses/${id}`);
  return data;
};

export const createExpense = async (payload) => {
  const { data } = await AZURE.post(`${BASE}/expenses`, payload);
  return data;
};

export const updateExpense = async (id, payload) => {
  const { data } = await AZURE.put(`${BASE}/expenses/${id}`, payload);
  return data;
};

export const deleteExpense = async (id) => {
  const { data } = await AZURE.delete(`${BASE}/expenses/${id}`);
  return data;
};

export const duplicateExpenseToScenario = async (id, scenarioType) => {
  const { data } = await AZURE.post(`${BASE}/expenses/${id}/duplicate-to-scenario`, { scenario_type: scenarioType });
  return data;
};

export const recalculateKPIs = async (referenceMonth = null, scenarioType = 'actual') => {
  const { data } = await AZURE.post(`${BASE}/expenses/recalculate`, { reference_month: referenceMonth, scenario_type: scenarioType });
  return data;
};

export const getExpensesSummary = async () => {
  const { data } = await AZURE.get(`${BASE}/expenses/summary`);
  return data;
};

// Unit Economics
export const getUnitEconomics = async (scenario = 'base') => {
  const { data } = await AZURE.get(`${BASE}/unit-economics`, { params: { scenario } });
  return data;
};

// Runway
export const getRunway = async (scenario = 'base') => {
  const { data } = await AZURE.get(`${BASE}/runway`, { params: { scenario } });
  return data;
};

// DRE
export const getProjectedDRE = async (months = 12, scenario = 'base') => {
  const { data } = await AZURE.get(`${BASE}/dre`, { params: { months, scenario } });
  return data;
};

// Investor Summary
export const getInvestorSummary = async (scenarioId = null) => {
  const params = scenarioId ? { scenario_id: scenarioId } : {};
  const { data } = await AZURE.get(`${BASE}/investor-summary`, { params });
  return data;
};

// ============ DETAILED STATEMENTS (v2) ============

// Chart of Accounts
export const getChartOfAccounts = async (type = null) => {
  const params = type ? { type } : {};
  const { data } = await AZURE.get(`${BASE}/chart-of-accounts`, { params });
  return data;
};

// DRE Detailed
export const getDREDetailed = async (months = 6, scenario = 'base', showReal = true) => {
  const { data } = await AZURE.get(`${BASE}/dre-detailed`, { params: { months, scenario, show_real: showReal } });
  return data;
};

// Cashflow Detailed
export const getCashflowDetailed = async (months = 6, scenario = 'base', showReal = true) => {
  const { data } = await AZURE.get(`${BASE}/cashflow-detailed`, { params: { months, scenario, show_real: showReal } });
  return data;
};

// Premises
export const listPremises = async (category = null, scenario = 'base') => {
  const params = { scenario };
  if (category) params.category = category;
  const { data } = await AZURE.get(`${BASE}/premises`, { params });
  return data;
};

export const upsertPremise = async (payload) => {
  const { data } = await AZURE.put(`${BASE}/premises`, payload);
  return data;
};

export const batchUpsertPremises = async (items) => {
  const { data } = await AZURE.put(`${BASE}/premises/batch`, { items });
  return data;
};

export const deletePremise = async (id) => {
  const { data } = await AZURE.delete(`${BASE}/premises/${id}`);
  return data;
};

// Generate Projection
export const generateProjection = async (months = 12, scenario = 'base') => {
  const { data } = await AZURE.post(`${BASE}/generate-projection`, { months, scenario });
  return data;
};

// Entries
export const getEntries = async (params = {}) => {
  const { data } = await AZURE.get(`${BASE}/entries`, { params });
  return data;
};

export const upsertEntry = async (payload) => {
  const { data } = await AZURE.put(`${BASE}/entries`, payload);
  return data;
};

// ============ NAMED SCENARIOS ============

export const listNamedScenarios = async () => {
  const { data } = await AZURE.get(`${BASE}/named-scenarios`);
  return data;
};

export const createNamedScenario = async (payload) => {
  const { data } = await AZURE.post(`${BASE}/named-scenarios`, payload);
  return data;
};

export const updateNamedScenario = async (id, payload) => {
  const { data } = await AZURE.put(`${BASE}/named-scenarios/${id}`, payload);
  return data;
};

export const deleteNamedScenario = async (id) => {
  const { data } = await AZURE.delete(`${BASE}/named-scenarios/${id}`);
  return data;
};

// ============ PRODUCT PROJECTIONS ============

export const listProductProjections = async (product = null, scenario = 'base', months = 12) => {
  const params = { scenario, months };
  if (product) params.product = product;
  const { data } = await AZURE.get(`${BASE}/product-projections`, { params });
  return data;
};

export const getProductSummary = async (scenario = 'base') => {
  const { data } = await AZURE.get(`${BASE}/product-projections/summary`, { params: { scenario } });
  return data;
};

export const upsertProductProjection = async (payload) => {
  const { data } = await AZURE.put(`${BASE}/product-projections`, payload);
  return data;
};

export const deleteProductProjections = async (productCode, scenarioName) => {
  const { data } = await AZURE.delete(`${BASE}/product-projections/${productCode}/${scenarioName}`);
  return data;
};

// ============ AZURE FX RATES (migration 313) ============

export const getAzureFXRates = async (months = 24) => {
  const { data } = await AZURE.get(`${BASE}/azure-fx/rates`, { params: { months } });
  return data;
};

export const upsertAzureFXRate = async (payload) => {
  // payload: { month_key, usd_brl, notes? }
  const { data } = await AZURE.put(`${BASE}/azure-fx/rates`, payload);
  return data;
};

export const freezeAzureFXMonth = async (monthKey, undo = false) => {
  const { data } = await AZURE.post(
    `${BASE}/azure-fx/rates/${monthKey}/freeze`,
    {},
    { params: undo ? { undo: '1' } : {} }
  );
  return data;
};

export const getAzureFXTotals = async (months = 13) => {
  const { data } = await AZURE.get(`${BASE}/azure-fx/totals`, { params: { months } });
  return data;
};

// ============ AZURE CREDIT CONFIG (migration 313) ============

export const getAzureCredits = async () => {
  const { data } = await AZURE.get(`${BASE}/azure-credits`);
  return data;
};

export const updateAzureCredits = async (payload) => {
  // payload: { total_usd?, spent_usd_override?, notes? }
  const { data } = await AZURE.put(`${BASE}/azure-credits`, payload);
  return data;
};
